import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, CardContent, CardHeader, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { FactCheck as CheckIcon } from '@mui/icons-material';
import { httpsCallable } from 'firebase/functions';
import { format } from 'date-fns';
import { functions } from '../../../shared/utils/firebase';
import { subscribeToSuccessUpdatesMeta } from '../../staffPerformance/services/successUpdatesService';
import { SuccessUpdateCounts, SuccessUpdatesMeta } from '../../staffPerformance/types';

interface CheckResult {
  status: 'ok' | 'error';
  staff: number;
  error?: string | null;
}

interface Outcome {
  severity: 'success' | 'warning' | 'error';
  message: string;
}

// The totals include the clients with no lead coach (the `__unassigned` bucket); the Staff Performance overview tile does not.
const totalsLine = (totals: SuccessUpdateCounts, unassignedActive: number) =>
  `${totals.overdue} overdue · ${totals.awaiting_first} awaiting first · ${totals.active} active clients` +
  (unassignedActive > 0 ? ` · incl. ${unassignedActive} with no lead coach` : '');

/**
 * Admin Panel → Data Sync. Shows when the overdue client-success updates were last
 * computed and runs the same Cloud Function the daily schedule uses ("Check now").
 */
const SuccessUpdatesSyncCard: React.FC = () => {
  const [meta, setMeta] = useState<SuccessUpdatesMeta | null>(null);
  const [metaError, setMetaError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  useEffect(() => {
    const unsubscribe = subscribeToSuccessUpdatesMeta(setMeta, (error) => setMetaError(error.message));
    return () => unsubscribe();
  }, []);

  const handleCheck = async () => {
    setRunning(true);
    setOutcome(null);
    try {
      const trigger = httpsCallable<{ force?: boolean }, CheckResult>(functions, 'triggerStaffSuccessUpdatesSync');
      const { data } = await trigger({});
      if (data.status === 'ok') {
        setOutcome({ severity: 'success', message: `Check finished: ${data.staff} ${data.staff === 1 ? 'coach' : 'coaches'} checked` });
      } else {
        setOutcome({ severity: 'warning', message: data.error || 'The check did not run.' });
      }
    } catch (error) {
      console.error('[staff-performance] manual success updates check failed', error);
      setOutcome({ severity: 'error', message: error instanceof Error ? error.message : 'Check failed. Check the function logs.' });
    } finally {
      setRunning(false);
    }
  };

  // The age of the data is the last SUCCESSFUL run; a failed run leaves the previous data in place.
  const statusChip = meta?.last_success_at ? (
    <Chip size="small" color={meta.status === 'ok' ? 'success' : 'warning'} label={`Checked ${format(meta.last_success_at, 'EEE d MMM, HH:mm')}`} />
  ) : (
    <Chip size="small" label="Never checked" />
  );

  return (
    <Card>
      <CardHeader
        avatar={<CheckIcon />}
        title="Client success updates"
        subheader="Overdue client updates per coach, shown on the Staff Performance page"
        action={
          <Button
            variant="contained"
            onClick={handleCheck}
            disabled={running}
            startIcon={running ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
            sx={{ mt: 1, mr: 1 }}
          >
            Check now
          </Button>
        }
      />
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            {statusChip}
            {meta?.totals ? (
              <Typography variant="body2" color="text.secondary">
                {totalsLine(meta.totals, meta.unassigned_active)}
              </Typography>
            ) : null}
          </Stack>

          {metaError ? <Alert severity="error">{metaError}</Alert> : null}
          {meta?.status === 'error' && meta.error ? <Alert severity="warning">{meta.error}</Alert> : null}
          {outcome ? <Alert severity={outcome.severity}>{outcome.message}</Alert> : null}

          <Typography variant="caption" color="text.secondary">
            Runs daily at 06:30 Singapore time.
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};

export { SuccessUpdatesSyncCard };
export default SuccessUpdatesSyncCard;
